import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Unlock, Copy, Check, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { decryptResponse } from "../lib/crypto";
import { copyToClipboard } from "../lib/clipboard";
import { useTranslation } from "react-i18next";


export const SecureRequestResult = () => {
  const { id } = useParams();
  const { t } = useTranslation("secureRequest");
  const GENERIC = t("result.generic");
  const wrapKeyRef = useRef(null);
  const recoveryTokenRef = useRef(null);
  const [hasKey, setHasKey] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [secret, setSecret] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Fragment lu en mémoire puis retiré immédiatement de l'URL. Jamais envoyé à l'API.
  // Formats supportés :
  //  - anonyme  : #k=<wrapKey>&r=<recoveryToken>
  //  - historique (workspace) : #<wrapKey>
  useEffect(() => {
    const raw = window.location.hash || "";
    const frag = raw.startsWith("#") ? raw.slice(1) : raw;
    if (frag) {
      if (frag.includes("k=") || frag.includes("r=")) {
        const params = new URLSearchParams(frag);
        wrapKeyRef.current = params.get("k") || null;
        recoveryTokenRef.current = params.get("r") || null;
      } else {
        wrapKeyRef.current = frag; // ancien format
      }
      if (wrapKeyRef.current) setHasKey(true);
      try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch (e) { /* noop */ }
    }
  }, []);

  const reveal = async () => {
    if (!wrapKeyRef.current) { setError(GENERIC); return; }
    setRevealing(true);
    try {
      // Le recovery token (anonyme) sert UNIQUEMENT d'autorisation ; la wrap key n'est jamais envoyée.
      const body = recoveryTokenRef.current ? { recovery_token: recoveryTokenRef.current } : {};
      const { data } = await api.post(`/secure-requests/${id}/reveal`, body);
      const plaintext = await decryptResponse({
        ciphertext: data.ciphertext, iv: data.iv, wrapped_key: data.wrapped_key,
        encrypted_private_key: data.encrypted_private_key, private_key_iv: data.private_key_iv,
        wrap_key: wrapKeyRef.current,
      });
      setSecret(plaintext);
    } catch (e) {
      const status = e.response?.status;
      if (status === 410 || status === 404 || status === 403 || status === 401) setError(t("result.unavailable"));
      else setError(GENERIC);
    } finally {
      setRevealing(false);
    }
  };

  const copy = async () => {
    const ok = await copyToClipboard(secret);
    if (ok) { setCopied(true); toast.success(t("result.copied")); setTimeout(() => setCopied(false), 2000); }
  };

  const wrap = (children) => (
    <div className="min-h-screen app-bg grain flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg glass rounded-2xl p-6 sm:p-8" data-testid="sr-result-card">{children}</div>
    </div>
  );

  if (secret !== null) return wrap(
    <div data-testid="sr-result-revealed">
      <div className="flex items-center gap-2 text-emerald-600 font-semibold mb-3"><Unlock className="w-5 h-5" /> {t("result.received")}</div>
      <div data-testid="sr-revealed-box" className="ph-no-capture font-mono-token text-sm break-all bg-slate-900 text-emerald-300 rounded-xl p-4 mb-3 whitespace-pre-wrap">{secret}</div>
      <Button data-testid="sr-copy-revealed" onClick={copy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} {t("result.copy")}
      </Button>
      <p className="text-[11px] text-slate-500 mt-3">
        {t("result.destroyed")}
      </p>
    </div>
  );

  if (error) return wrap(
    <div data-testid="sr-result-error" className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4 mx-auto"><AlertTriangle className="w-7 h-7 text-red-600" /></div>
      <h2 className="font-heading text-xl font-bold mb-2">{t("result.unavailableTitle")}</h2>
      <p className="text-sm text-slate-500">{error}</p>
    </div>
  );

  if (!hasKey) return wrap(
    <div data-testid="sr-result-missing-key" className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4 mx-auto"><AlertTriangle className="w-7 h-7 text-red-600" /></div>
      <h2 className="font-heading text-xl font-bold mb-2">{t("result.incomplete")}</h2>
      <p className="text-sm text-slate-500">{GENERIC}</p>
    </div>
  );

  return wrap(
    <div className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-4 mx-auto"><Lock className="w-7 h-7 text-amber-600" /></div>
      <h2 className="font-heading text-xl font-bold mb-2">{t("result.availableTitle")}</h2>
      <p className="text-sm text-slate-500 mb-5">{t("result.oneShot")}</p>
      <Button data-testid="sr-reveal-btn" onClick={reveal} disabled={revealing} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
        {revealing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />} {t("result.reveal")}
      </Button>
    </div>
  );
};
